import React, { useMemo, useState, type ReactNode } from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableHeadCell,
  TableHeader,
  TableRow,
  SortableHeadCell,
  TableFooter,
} from "./table.tsx";
import type { DataTableColumn, SortDirection } from "./types.ts";

export function compareValues(a: unknown, b: unknown): number {
  if (a === b) return 0;
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() - b.getTime();
  }
  if (typeof a === "number" && typeof b === "number") {
    return a - b;
  }
  return String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

function nextSortState(
  columnId: string,
  sortKey: string | undefined,
  sortDir: SortDirection
): { sortKey: string | undefined; sortDir: SortDirection } {
  if (sortKey !== columnId) {
    return { sortKey: columnId, sortDir: "asc" };
  }
  if (sortDir === "asc") {
    return { sortKey: columnId, sortDir: "desc" };
  }
  if (sortDir === "desc") {
    return { sortKey: undefined, sortDir: null };
  }
  return { sortKey: columnId, sortDir: "asc" };
}

export type DataTableProps<T extends Record<string, unknown>> = {
  columns: DataTableColumn<T>[];
  data: T[];
  sortKey?: string;
  sortDir?: SortDirection;
  onSortChange?: (sortKey: string | undefined, sortDir: SortDirection) => void;
  /** Uncontrolled: initial sort column id */
  defaultSortKey?: string;
  /** Uncontrolled: initial direction */
  defaultSortDir?: Exclude<SortDirection, null>;
  className?: string;
  "aria-label"?: string;
  footer?: React.ReactNode;
};

export function DataTable<T extends Record<string, unknown>>({
  columns,
  data,
  sortKey: sortKeyProp,
  sortDir: sortDirProp,
  onSortChange,
  defaultSortKey,
  defaultSortDir,
  className,
  "aria-label": ariaLabel,
  footer
}: DataTableProps<T>) {
  const uncontrolled = onSortChange == null;
  const [internalSortKey, setInternalSortKey] = useState<string | undefined>(
    () => defaultSortKey
  );
  const [internalSortDir, setInternalSortDir] = useState<SortDirection>(
    () => defaultSortDir ?? null
  );

  const sortKey = uncontrolled ? internalSortKey : sortKeyProp;
  const sortDir = uncontrolled
    ? internalSortDir
    : (sortDirProp ?? null) as SortDirection;

  const displayData = useMemo(() => {
    if (!uncontrolled || !sortKey || sortDir == null) {
      return data;
    }
    const col = columns.find((c) => c.id === sortKey);
    const getVal = (row: T) =>
      col?.getSortValue != null
        ? col.getSortValue(row)
        : col?.accessor != null
          ? row[col.accessor]
          : null;
    if (col?.getSortValue == null && col?.accessor == null) {
      return data;
    }
    const copy = [...data];
    copy.sort((a, b) => {
      const cmp = compareValues(getVal(a), getVal(b));
      return sortDir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [uncontrolled, data, columns, sortKey, sortDir]);

  const onHeaderSort = (col: DataTableColumn<T>) => {
    if (!col.sortable) return;
    if (uncontrolled && col.accessor == null && col.getSortValue == null) return;

    const { sortKey: nextKey, sortDir: nextDir } = nextSortState(
      col.id,
      sortKey,
      sortDir
    );

    if (uncontrolled) {
      setInternalSortKey(nextKey);
      setInternalSortDir(nextDir);
    } else {
      onSortChange?.(nextKey, nextDir);
    }
  };

  const headerFor = (col: DataTableColumn<T>): ReactNode => {
    if (col.sortable) {
      return (
        <SortableHeadCell
          key={col.id}
          columnId={col.id}
          sortable
          sortDirection={sortKey === col.id ? sortDir : null}
          onSort={() => onHeaderSort(col)}
        >
          {col.header}
        </SortableHeadCell>
      );
    }
    return <TableHeadCell key={col.id}>{col.header}</TableHeadCell>;
  };

  return (
    <Table className={className} aria-label={ariaLabel}>
      <TableHeader>
        <TableRow>{columns.map((col) => headerFor(col))}</TableRow>
      </TableHeader>
      <TableBody>
        {displayData.map((row, rowIndex) => (
          <TableRow key={rowIndex}>
            {columns.map((col) => (
              <TableCell key={col.id}>
                {col.render
                  ? col.render(row, rowIndex)
                  : col.accessor != null
                    ? (row[col.accessor] as ReactNode)
                    : null}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
      {/* Fixed: Footer now directly accepts TableRows to enable column-grid alignments */}
      {footer && <TableFooter>{footer}</TableFooter>}
    </Table>
  );
}
